import React from 'react';
import { Link } from 'react-router-dom';
import { ComingSoonPage } from '@components/ui/pages';
import { useFeatureFlag } from '@features/user';
import { Button, Typography } from '@mui/material';
import { useEventTracking } from '@services/statsig';

import { useTracking } from '../../../context/TrackingContext';
import { EventType } from '../../../types/events';

/**
 * AuditPage Component
 *
 * A "coming soon" page for the Audit feature.
 * Displays information about the upcoming audit feature
 * with CTAs for learning more and accessing rules.
 */
export const AuditPage: React.FC = () => {
  const { audit } = useEventTracking();
  const { trackEvent } = useTracking();
  const isPayerTemplateProcessingEnabled = useFeatureFlag('payer_template_note_processing');

  const handleLearnMoreClick = () => {
    audit.learnMoreClicked({
      destination_url: 'https://calendly.com/jackson-smartscribe/30min',
    });
    trackEvent(EventType.EXPRESSED_INTEREST, {
      feature: 'jotaudit',
    });
  };

  const handleSendDocumentsClick = () => {
    audit.sendDocumentsClicked();
    trackEvent(EventType.EXPRESSED_INTEREST, {
      feature: 'jotaudit',
    });
  };

  const handleRulesClick = () => {
    audit.rulesClicked({
      destination_path: '/rules',
    });
  };

  return (
    <ComingSoonPage
      title="JotAudit"
      showHeader={false}
      action={
        isPayerTemplateProcessingEnabled ? (
          <Button
            component={Link}
            to="/rules"
            variant="contained"
            color="secondary"
            onClick={handleRulesClick}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 2,
            }}
          >
            Your Rules
          </Button>
        ) : undefined
      }
      heroTitle="JotAudit"
      description={
        <>
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary', mb: 3 }}>
            Catch audit risk before submitting a claim.
          </Typography>
          <Typography component="p" sx={{ mb: 2 }}>
            <strong> JotAudit is an AI audit engine built for behavioral-health clinics.</strong> It
            evaluates every note the way your payers do — ensuring coding accuracy, compliance, and
            defensibility.
          </Typography>
          <Typography component="p" sx={{ mb: 2 }}>
            JotAudit starts with a <strong>robust, off-the-shelf library of 150+ rules</strong> from
            CMS, APA, Cigna, Aetna, Optum, and other payers’ public guidelines. You can then{' '}
            <strong>
              add your own documentation expectations, policies, or compliance requirements
            </strong>
            , and the engine automatically scores each note against them.
          </Typography>
          <Typography component="p">
            Your first 10 note audits are free, then <strong>just $29 per seat per month.</strong>
          </Typography>
        </>
      }
      primaryAction={{
        label: 'Learn more',
        href: 'https://calendly.com/jackson-smartscribe/30min',
        external: true,
        onClick: handleLearnMoreClick,
        startIcon: null,
      }}
      secondaryAction={{
        label: 'Send us your documents',
        href: 'mailto:docs@jotpsych.com',
        onClick: handleSendDocumentsClick,
        variant: 'outlined',
      }}
    />
  );
};
